import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bed, Megaphone, CircleNotch } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";

export function VacantProperties() {
  const [vacant, setVacant] = useState(null);
  const [relisting, setRelisting] = useState(null);
  const { fmt } = useCurrency();

  const load = () => api.get("/vacant").then(({ data }) => setVacant(data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // real-time refresh as bookings end / are cancelled
    return () => clearInterval(t);
  }, []);

  const relist = async (room) => {
    setRelisting(room.id);
    try {
      const { data } = await api.post(`/rooms/${room.id}/relist`);
      if (data.status === "listed") {
        toast.success(`Room ${data.room} republished on EasyStay`);
        load();
      } else {
        toast.warning(data.detail || "Relist queued — EasyStay endpoint pending", { duration: 6000 });
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRelisting(null);
    }
  };

  if (!vacant || vacant.length === 0) return null;

  return (
    <div className="border border-status-pending/40 bg-card" data-testid="vacant-properties">
      <div className="flex items-center justify-between border-b border-border p-5">
        <div>
          <h2 className="font-heading text-lg font-bold">Vacant Properties</h2>
          <p className="text-xs text-muted-foreground">
            These listings were paused when their booking ended or was cancelled — republish them on EasyStay with one click.
          </p>
        </div>
        <span className="rounded-sm border border-status-pending/40 bg-status-pending/10 px-2 py-1 text-xs font-semibold text-status-pending" data-testid="vacant-count">
          {vacant.length} vacant
        </span>
      </div>
      <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2 xl:grid-cols-3">
        {vacant.map((room) => (
          <div key={room.id} className="border border-border p-4" data-testid={`vacant-room-${room.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bed size={18} weight="duotone" className="text-primary" />
                <span className="font-heading font-bold">Room {room.name}</span>
              </div>
              <span className="text-xs uppercase tracking-wider text-muted-foreground">{room.type}</span>
            </div>
            <div className="mt-2 text-sm">
              <span className="font-semibold text-primary">{fmt(room.rate)}</span>
              <span className="text-xs text-muted-foreground"> / night · {room.capacity} guests</span>
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {room.last_guest ? `Last guest: ${room.last_guest}` : "No recent booking"}
              {room.vacant_since ? ` · vacant since ${room.vacant_since}` : ""}
            </div>
            <Button onClick={() => relist(room)} disabled={relisting === room.id}
              data-testid={`relist-${room.id}`} className="mt-3 w-full transition-colors">
              {relisting === room.id ? (
                <CircleNotch size={15} className="mr-1.5 animate-spin" />
              ) : (
                <Megaphone size={15} className="mr-1.5" />
              )}
              {relisting === room.id ? "Republishing…" : "Relist on EasyStay"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
